import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';
import BaseEmployeeStore from '../../../../shared/stores/baseEmployeeStore';

// Todo: was ist für den Fall das keine Daten geladen werden können?
class OrderStore extends BaseEmployeeStore {
    public objectListLoading: boolean = false;
    public objectList: Array<any> = [];
    public selectedObject: any = null;
    public representationForObject: string = '';

    public productListLoading: boolean = false;
    public productList: Array<any> | null = null;

    public productCount: Array<any> = [];

    public comment: string = '';

    public orderLoading: boolean = false;

    public productQuickSearch: string = '';
    public s3url: string = '';

    public selectedCategory: { id: number | null; name: string } | null = null;

    public orderFinished: boolean = false;

    constructor(sdk: ApplicationSdk) {
        super(sdk);

        makeObservable(this, {
            s3url: observable,
            objectList: observable,
            comment: observable,
            orderLoading: observable,
            selectedObject: observable,
            selectedCategory: observable,
            representationForObject: observable,
            productList: observable,
            productQuickSearch: observable,
            productCount: observable,
            selectObject: action,
            setComment: action,
            setProductQuickSearch: action,
            setRepresentationForObject: action,
            incrementProduct: action,
            decrementProduct: action,
            setSelectedCategory: action,
            finish: action,
            orderable: computed,
            allProducts: computed,
            categories: computed,
            isRepresentationObject: computed,
            isRepresentationObjectValid: computed,
            orderFinished: observable,
        });

        this.objectList = [];
        this.productList = null;
        this.comment = '';
        this.orderLoading = false;
    }

    public async onInit(): Promise<void> {
        this.updateFooterButton({
            label: 'Bestellen',
            icon: 'pi pi-shopping-cart',
            onClick: () => this.handleCreateOrder(),
        });
        const menuItem = this.sdk.findMenuItemByKey('neworder');
        if (menuItem) {
            this.setMenuConfig({
                customHeader: {
                    title: menuItem.label,
                    icon: menuItem.icon,
                },
            });
        }
        this.fetchObjectList();
    }

    public setSelectedCategory(category: { id: number; name: string } | null) {
        this.selectedCategory = category;

        if (this.selectedCategory?.id === null) {
            this.selectedCategory = null;
        }
    }

    public selectObject(value: any): void {
        if (value?.id !== this.selectedObject?.id) {
            this.selectedObject = value;
            this.productList = [];
            this.fetchProductList(value.id);
        }
    }

    public setRepresentationForObject(value: string): void {
        this.representationForObject = value;
    }

    public async fetchObjectList(): Promise<void> {
        runInAction(() => {
            this.objectListLoading = true;
        });
        const objectListResult: any = await this.sdk.fetchApi('object', 'query', {
            sortvalue: 'name',
            sort: 'asc',
            filter: {
                type: 'employee',
                value: this.sdk?.user?.id,
            }, // Todo: Change with my employee id
        });

        runInAction(() => {
            this.objectListLoading = false;

            if (objectListResult?.status === 200) {
                this.objectList = objectListResult.json.data;
            }
        });
    }

    public async fetchProductList(object_id: number): Promise<void> {
        runInAction(() => {
            this.productListLoading = true;
        });
        const productListResult: any = await this.sdk.fetchApi('object', 'getproducts', {
            object_id,
        });

        runInAction(() => {
            this.productListLoading = false;
            if (productListResult?.status === 200) {
                this.productList = productListResult.json.assigned_products;
                this.s3url = productListResult.json?.s3url || '';
            }
        });
    }

    public async handleCreateOrder(): Promise<any> {
        const result = await this.createOrder();
        if (result) {
            this.showSuccess('Bestellung erfolgreich erstellt');
            this.finish();
        } else {
            this.showError('Fehler beim Bestellen');
        }
    }

    public async createOrder(): Promise<any> {
        runInAction(() => {
            this.orderLoading = true;
        });

        const newOrderResult: any = await this.sdk.fetchApi('order', 'add', {
            comment: this.comment,
            object_id: this.selectedObject.id,
            object_name: this.representationForObject || null,
            employee_id: this.sdk?.user?.id,
            products: this.productCount.map((item: any) => {
                return { product_id: item.id, units: item.count };
            }),
        });

        runInAction(() => {
            this.orderLoading = false;
        });

        if (newOrderResult?.status === 201) {
            return newOrderResult.json;
        }

        return false;
    }

    public setComment(value: string): void {
        this.comment = value;
    }

    public setProductQuickSearch(value: string): void {
        this.productQuickSearch = value;
    }

    public incrementProduct(product: any): void {
        const useProduct: any = this.productCount.find((item: any) => item.id === product.id);
        let updated: boolean = false;
        if (!useProduct) {
            this.productCount.push({ id: product.id, count: 1 });
        } else {
            useProduct.count++;
        }
        if (updated) {
            this.productCount = this.productCount.map(x => x).filter((item: any) => item.count > 0);
        }
    }

    public decrementProduct(product: any): void {
        const useProduct: any = this.productCount.find((item: any) => item.id === product.id);
        let updated: boolean = false;
        if (useProduct) {
            if (useProduct.count > 0) {
                useProduct.count--;
                updated = true;
            }
        }

        if (updated) {
            this.productCount = this.productCount.map(x => x).filter((item: any) => item.count > 0);
        }
    }

    public finish(): void {
        this.objectListLoading = false;
        this.productListLoading = false;
        this.orderLoading = false;
        this.productCount = [];
        this.comment = '';
        this.productList = null;
        this.selectedObject = null;
        this.orderFinished = true;
    }

    get orderable(): boolean {
        return (
            this.selectedObject &&
            this.productCount.length > 0 &&
            !!this.productCount.find((x: any) => x.count > 0)
        );
    }

    get allProducts(): Array<any> | null {
        if (!this.productList) return null;
        return this.productList
            .filter((item: any) => {
                return (
                    (item.name.toLowerCase().includes(this.productQuickSearch.toLowerCase()) ||
                        item?.description
                            ?.toLowerCase()
                            ?.includes(this.productQuickSearch.toLowerCase())) &&
                    (this.selectedCategory === null ||
                        item.category.id === this.selectedCategory.id)
                );
            })
            .map((item: any) => ({
                ...item,
                image: item.image ? `${this.s3url}${item.image}.jpeg` : null,
            }));
    }

    get categories(): Array<{ id: number; name: string }> {
        if (!this.productList) return [];

        // Erstelle eine Map, um Duplikate zu vermeiden
        const categoryMap = new Map<number, string>();

        this.productList.forEach((product: any) => {
            if (product.category) {
                categoryMap.set(product.category.id, product.category.name);
            }
        });
        // Konvertiere die Map in ein Array
        return Array.from(categoryMap, ([id, name]) => ({ id, name }));
    }

    get isRepresentationObject(): boolean {
        return this.selectedObject?.representation === true;
    }

    get isRepresentationObjectValid(): boolean {
        return this.isRepresentationObject && !!this.representationForObject;
    }

    get isBackButtonVisible(): boolean {
        return true;
    }

    get isFooterButtonVisible(): boolean {
        if (this.orderFinished) return false;
        return true;
    }

    get isFooterButtonEnabled(): boolean {
        return this.orderable;
    }

    get isFooterButtonLoading(): boolean {
        return this.orderLoading;
    }

    get footerButtonHelpText(): string {
        return this.orderable
            ? 'Bestellung abschließen'
            : 'Bitte wählen Sie ein Objekt und mindestens 1 Produkt aus';
    }

    protected async onBeforeNavigateBack(): Promise<boolean> {
        if (!this.orderable) return true;

        const result = await this.sdk.getDialogConfirm({
            title: 'Bestellung abbrechen?',
            message:
                'Möchten Sie die Bestellung wirklich abbrechen? Alle Änderungen gehen verloren.',
            onConfirm: async () => ({ success: true, status: 200 }),
        });

        return result.confirmed || false;
    }
}

export default OrderStore;
